import React, {useState} from 'react';
import clsx from 'clsx'
import { Control, Controller } from "react-hook-form";
import {IconButton, InputAdornment, Stack, StandardTextFieldProps, TextField} from "@mui/material";
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import Icon from 'src/@core/components/icon'
import ControlledPasswordFieldStyles from "./ControlledPasswordField.styles";

export interface ControlledTextPasswordFieldProps extends StandardTextFieldProps {
    control: Control<any>,
    showRules?: boolean
}

export interface PasswordRules {
    minLength: boolean,
    hasLowercase: boolean,
    hasUppercase: boolean,
    hasNumber: boolean,
    hasSpecialChar: boolean,
}

export function ControlledPasswordField(props: ControlledTextPasswordFieldProps) {
    const [showPassword, setShowPassword] = useState<boolean>(false);
    
    const [rules, setRules] = useState<PasswordRules>({
        minLength: false,
        hasLowercase: false,
        hasUppercase: false,
        hasNumber: false,
        hasSpecialChar: false
    });

    const handleClickShowPassword = () => setShowPassword(!showPassword);

    const handlePasswordChange = (event: any) => {
        const value = event.target.value ?? "";

        setRules({
            minLength: value.length >= 8,
            hasLowercase: /[a-z]/.test(value),
            hasUppercase: /[A-Z]/.test(value),
            hasNumber: /\d/.test(value),
            hasSpecialChar: /[!?+$%_.\/,~()=#{}@^*]/.test(value),
        });
    };
    
    return (
        <Controller
            control={props.control}
            name={props.name || ''}
            render={({field: {onChange, value}, fieldState: {error}}) => (
                <Stack>
                    <TextField
                        {...props}
                        variant="outlined"
                        size="small"
                        type={showPassword ? 'text' : 'password'}
                        value={value}
                        onChange={(e) => {
                            props.showRules && handlePasswordChange(e);
                            onChange(e);
                        }}
                        error={!!error}
                        defaultValue={props.defaultValue}
                        helperText={error ? error.message : null}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={handleClickShowPassword}
                                        edge="end" >
                                        { showPassword ? <VisibilityOffIcon /> : <VisibilityIcon /> }
                                    </IconButton>
                                </InputAdornment>
                            ),
                            ...props.InputProps
                        }}
                        InputLabelProps={{
                            shrink: value+1,
                            ...props.InputLabelProps,
                        }}
                    >
                    </TextField>

                    {
                        props.showRules &&
                            <Stack>
                                <PasswordRule label={"Al menos 8 caracteres"}
                                              valid={rules.minLength} />
                                <PasswordRule label={"Al menos 1 letra mayúscula"}
                                              valid={rules.hasUppercase} />
                                <PasswordRule label={"Al menos 1 letra minúscula"}
                                              valid={rules.hasLowercase} />
                                <PasswordRule label={"Al menos 1 número"}
                                              valid={rules.hasNumber} />
                                <PasswordRule label={"Al menos 1 carácter especial (por ejemplo, !@#$+-)"}
                                              valid={rules.hasSpecialChar} />
                            </Stack>
                    }
                </Stack>
            )}
        />
    );
}

interface PasswordRuleProps {
    label: string,
    valid: boolean
}

function PasswordRule({ label, valid }: PasswordRuleProps) {
    const classes = ControlledPasswordFieldStyles();
    
    return (
        <Stack direction={'row'} alignItems={'center'} spacing={1} pl={2}
               className={clsx(classes.ruleItem, {
                   [classes.ruleItemValid]: valid
               })}
        >
            {
                valid ?
                    <Icon icon={"mdi:check-circle-outline"}/>
                    :
                    <Icon icon={"mdi:close-circle-outline"}/>
            }

            <span>{label}</span>
        </Stack>
    )
}